import type {
    Characteristic,
    Component,
    MatrixValueCharacteristic,
    Value,
} from '../schema';
import type { EvaluatedMap } from '../eval/types';
import { pickMatrixIndex, selectMatrixTable } from '../eval/evaluator';

/**
 * Per-Value matrix-driven price lookup ("Preise für Wert aus Matrix").
 *
 * The legacy storefront's `option/abstract.js:calculate` walks each choice,
 * resolves `(dim_x, dim_y)` coordinates, and reads the price from a
 * referenced matrixvalue Characteristic. V3 mirrors that — except dim_y
 * (and any non-sentinel dim_x) come from the evaluator's already-computed
 * `EvaluatedMap` rather than walking the live config tree.
 *
 * `Xcurrent_choice_valueX` is a legacy sentinel meaning "use this Value's
 * own `.value` field" — supports the common pattern of a price list
 * indexed by `(value_of_this_choice, value_of_some_other_char)`.
 *
 * Returns `null` when any link in the chain is missing (matrix not found,
 * dim not resolvable, no cell). Callers fall back to `0` or to the
 * Value's static `.price` as appropriate.
 */
export function lookupChoiceMatrixPrice(
    value: Value,
    matrixAltTitle: string,
    dimensionX: string,
    dimensionY: string,
    component: Component,
    evaluated: EvaluatedMap,
): number | null {
    const matrixChar = findCharacteristicByAltTitle(component, matrixAltTitle);
    if (!matrixChar || matrixChar.type !== 'matrixvalue') return null;

    const xVal = resolveDimension(dimensionX, value, component, evaluated);
    const yVal = resolveDimension(dimensionY, value, component, evaluated);
    if (xVal == null || yVal == null) return null;

    // The referenced matrix may switch tables via its control list — follow
    // it here too, the same way the matrix itself does.
    const control = matrixChar.controlMatrix
        ? findCharacteristicByAltTitle(component, matrixChar.controlMatrix.replace(/^\$+/, ''))
        : undefined;
    const table = selectMatrixTable(matrixChar, control ? evaluated.get(control.id) ?? null : null);

    return readMatrixCell(matrixChar, table, xVal, yVal);
}

function resolveDimension(
    dim: string,
    value: Value,
    component: Component,
    evaluated: EvaluatedMap,
): number | string | null {
    if (dim === 'Xcurrent_choice_valueX') {
        // The Value's own `.value` field. Numeric strings parse so a price
        // matrix indexed by integer widths matches the numeric value
        // 1234 against the value-string "1234"; non-numeric codes stay
        // string-typed for the matrix's `match` operator.
        if (value.value == null || value.value === '') return null;
        const n = Number(value.value);
        return Number.isFinite(n) ? n : value.value;
    }
    const dep = findCharacteristicByAltTitle(component, dim);
    if (!dep) return null;
    const v = evaluated.get(dep.id);
    if (v == null) return null;
    // Evaluator already returns number-or-string per Characteristic — pass
    // through unchanged so pickMatrixIndex can pick the right operator path.
    if (typeof v === 'number' || typeof v === 'string') return v;
    return null;
}

function readMatrixCell(
    matrix: MatrixValueCharacteristic,
    table: { data: unknown[][] } | undefined,
    x: number | string,
    y: number | string,
): number | null {
    if (!table || table.data.length < 2) return null;

    const xOp = matrix.matrixOperatorX ?? 'match';
    const yOp = matrix.matrixOperatorY ?? 'match';

    // Layout convention: row 0 = X-axis headers (skip cell [0][0]),
    // col 0 = Y-axis row labels.
    const headerRow = table.data[0] ?? [];
    const colIdx = pickMatrixIndex(xOp, x, headerRow.slice(1));
    if (colIdx === null) return null;
    const targetCol = colIdx + 1;

    const bodyRows = table.data.slice(1);
    const rowHeaders = bodyRows.map((row) => row[0]);
    const rowIdx = pickMatrixIndex(yOp, y, rowHeaders);
    if (rowIdx === null) return null;

    const cell = bodyRows[rowIdx]?.[targetCol];
    if (cell == null || cell === '') return null;
    const n = typeof cell === 'number' ? cell : Number(String(cell).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
}

function findCharacteristicByAltTitle(
    component: Component,
    altTitle: string,
): Characteristic | undefined {
    for (const group of component.groups) {
        for (const c of group.characteristics) {
            if (c.altTitle === altTitle) return c;
        }
    }
    return undefined;
}

/**
 * Effective Value-price resolver. When the Characteristic has a
 * `choicePriceMatrix` config, the result of the matrix lookup wins over
 * `value.price`. When the config is missing or the lookup fails, falls
 * back to `value.price`.
 *
 * Returns `0` on any failure path so the pricing engine's downstream sum
 * stays a finite number.
 */
export function effectiveValuePrice(
    value: Value,
    characteristic: Characteristic,
    component: Component,
    evaluated: EvaluatedMap,
): number {
    if ('choicePriceMatrix' in characteristic && characteristic.choicePriceMatrix) {
        const { matrix, dimensionX, dimensionY } = characteristic.choicePriceMatrix;
        const looked = lookupChoiceMatrixPrice(
            value, matrix, dimensionX, dimensionY, component, evaluated,
        );
        if (looked !== null) return looked;
        return 0;
    }
    return parsePrice(value.price);
}

function parsePrice(raw: string | undefined): number {
    if (raw == null || raw === '') return 0;
    const cleaned = raw.endsWith('P') ? raw.slice(0, -1) : raw;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
}
